<script setup lang="ts">
import { computed } from "vue";

/**
 * Reusable animated badge component
 */
interface BadgeProps {
  /**
   * Visual variant of the badge
   */
  variant?: Variants;
}

type Variants = "ongoing" | "completed" | "attended";

const props = withDefaults(defineProps<BadgeProps>(), {
  variant: "ongoing",
});

const variantClasses: Record<Variants, string> = {
  ongoing: "bg-gradient-to-r from-green-500 to-emerald-600 text-white",
  completed:
    "border border-emerald-500 bg-emerald-100 dark:border-emerald-600 dark:bg-emerald-900 text-emerald-600 dark:text-emerald-400",
  attended:
    "border border-gray-400 bg-gray-200/50 dark:border-gray-500 dark:bg-gray-500 text-gray-500 dark:text-gray-100",
};

const isAnimated = computed(() => props.variant === "ongoing");
const badgeClasses = computed(() => variantClasses[props.variant]);
</script>

<template>
  <div class="flex flex-col gap-y-2">
    <span
      class="grow-0 flex items-center px-3 py-1 rounded-full text-xs lg:text-[11px] font-semibold"
      :class="[
        badgeClasses,
        {
          'animate-pulse': isAnimated,
          'shadow-sm': props.variant === 'ongoing',
        },
      ]"
    >
      <span
        v-if="props.variant === 'ongoing'"
        class="w-2 h-2 bg-white rounded-full mr-2 block"
      ></span>
      <slot>Ongoing</slot>
    </span>
  </div>
</template>
